import type { ThemeOverride } from '@chakra-ui/react';

/**
 * `-webkit-touch-callout` only exists in iOS/iPadOS WebKit, so rules under
 * this query never reach desktop browsers or Android.
 */
const IOS_ONLY = '@supports (-webkit-touch-callout: none)';

/**
 * `--viewport-height` is always a length (`100vh`, or `100dvh` where
 * supported), unlike Chakra's `--chakra-vh`, which can resolve to
 * `-webkit-fill-available` and is therefore invalid inside `calc()`. Use
 * `calc(var(--viewport-height) - Npx)` for scroll regions sized from the
 * viewport.
 */
const VIEWPORT_HEIGHT_VAR = '--viewport-height';

/**
 * Chakra theme override that makes drawers and forms usable in iOS Safari.
 * Spread it into every web app's `extendTheme(...)` call.
 *
 * Rule 1 is iOS-only. Rule 2 applies on every platform: it only swaps the
 * static `100vh` for the dynamic viewport height that Chakra's own drawer
 * container already uses, so the panel can never be taller than its
 * container (this also helps Android Chrome while its URL bar is visible).
 *
 * 1. iOS Safari zooms the page when a focused `input`/`select`/`textarea`
 *    renders text below 16px. Chakra's modal scroll lock ships with
 *    `allowPinchZoom={false}`, so the user cannot pinch back out while a
 *    drawer is open. Forcing 16px on iOS removes the zoom at the source
 *    without touching the desktop/Android look.
 * 2. Chakra's drawer panel is hard-coded to `100vh`, which in iOS Safari
 *    includes the area behind the browser toolbars. The panel then overflows
 *    the visible viewport: the body stops being scrollable (the scroll lock
 *    only lets scrollable children move) and the footer buttons sit
 *    off-screen. `$100vh` resolves to Chakra's `--chakra-vh` variable
 *    (`100dvh` where supported, `-webkit-fill-available` otherwise), which
 *    the drawer container already uses.
 */
export const mobileSafariTheme: ThemeOverride = {
  styles: {
    global: {
      ':root': { [VIEWPORT_HEIGHT_VAR]: '100vh' },
      '@supports (height: 100dvh)': {
        ':root': { [VIEWPORT_HEIGHT_VAR]: '100dvh' },
      },
      [IOS_ONLY]: {
        'input, select, textarea': { fontSize: '16px !important' },
      },
    },
  },
  components: {
    Drawer: {
      baseStyle: (props) => ({
        dialog: {
          ...(props.isFullHeight && { height: '$100vh' }),
          maxH: '$100vh',
        },
        body: { overscrollBehavior: 'contain' },
      }),
      sizes: {
        full: { dialog: { h: '$100vh' } },
      },
    },
  },
};
